import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, User, Loader2, X } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalNewsDesk = () => {
  const [joiners, setJoiners] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const token = localStorage.getItem('portal_token');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/news-desk`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJoiners(res.data.joiners || []);
        setAnnouncements(res.data.custom_announcements || []);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
      </div>
    );
  }

  const hasContent = joiners.length > 0 || announcements.length > 0;

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-news-desk">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Newspaper className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">HR News Desk</h2>
          <p className="text-xs text-gray-400">Announcements and updates</p>
        </div>
      </div>

      {!hasContent ? (
        <Card className="border-dashed border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <Newspaper className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No announcements at this time</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Announcements */}
          {announcements.map(item => {
            const imgSrc = item.image_url
              ? (item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`)
              : null;
            return (
            <Card key={item.id} className="border-0 shadow-sm" data-testid={`news-announcement-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Newspaper className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed whitespace-pre-line">{item.message}</p>
                    {imgSrc && (
                      <div
                        className="mt-2 cursor-pointer rounded-lg overflow-hidden border border-gray-100 inline-block"
                        onClick={() => setLightboxImg(imgSrc)}
                        data-testid={`news-image-${item.id}`}
                      >
                        <img
                          src={imgSrc}
                          alt={item.title}
                          className="max-h-48 max-w-full object-cover rounded-lg hover:opacity-90 transition"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[9px] border-amber-200 text-amber-600">Announcement</Badge>
                      {item.department && item.department !== 'ALL' && (
                        <Badge variant="outline" className="text-[9px] text-gray-400">{item.department}</Badge>
                      )}
                      {item.created_at && (
                        <span className="text-[9px] text-gray-400">
                          {new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}

          {/* New Joiners */}
          {joiners.map(item => {
            const joinDate = new Date(item.join_date + 'T00:00:00');
            const formatted = joinDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            return (
              <Card key={item.id} className="border-0 shadow-sm" data-testid={`news-joiner-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.photo_url ? (
                        <img src={`${API}${item.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <User className="h-4 w-4 text-indigo-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">
                        Welcome <span className="text-indigo-600">{item.name}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.position} &bull; {item.department}</p>
                      <p className="text-[10px] text-gray-400 italic mt-1.5 leading-relaxed">
                        Welcome to the team! We're excited to have you on board and look forward to your contributions.
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[9px] border-indigo-200 text-indigo-600">New Joiner</Badge>
                        <span className="text-[9px] text-gray-400">{formatted}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setLightboxImg(null)}
          data-testid="image-lightbox"
        >
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
            data-testid="lightbox-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxImg}
            alt="Enlarged"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      )}
    </div>
  );
};

export default PortalNewsDesk;
